import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMenus, getCategoriesByMenu, getDishesByCategory } from '../services/api';
import { useTranslation } from '../contexts/LanguageContext';

const Dashboard = () => {
  const navigate = useNavigate();
  const { language, changeLanguage, t } = useTranslation();
  const [stats, setStats] = useState({
    menus: 0,
    categories: 0,
    dishes: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const menusRes = await getMenus();
        let totalCategories = 0;
        let totalDishes = 0;

        for (const menu of (menusRes.data || [])) {
          const categoriesRes = await getCategoriesByMenu(menu._id);
          totalCategories += (categoriesRes.data || []).length;
          
          for (const category of (categoriesRes.data || [])) {
            const dishesRes = await getDishesByCategory(category._id);
            totalDishes += (dishesRes.data || []).length;
          }
        }

        setStats({
          menus: (menusRes.data || []).length,
          categories: totalCategories,
          dishes: totalDishes
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 className="page-title">{t('dashboard')}</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => changeLanguage('en')}
            style={{
              padding: '8px 16px',
              backgroundColor: language === 'en' ? 'var(--primary-color)' : 'transparent',
              color: language === 'en' ? 'var(--text-primary)' : 'var(--text-secondary)',
              border: `1px solid var(--border-color)`,
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'all 0.3s ease'
            }}
          >
            English
          </button>
          <button
            onClick={() => changeLanguage('ar')}
            style={{
              padding: '8px 16px',
              backgroundColor: language === 'ar' ? 'var(--primary-color)' : 'transparent',
              color: language === 'ar' ? 'var(--text-primary)' : 'var(--text-secondary)',
              border: `1px solid var(--border-color)`,
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              transition: 'all 0.3s ease'
            }}
          >
            العربية
          </button>
        </div>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '25px' }}>
        <div className="card" style={{ 
          background: 'var(--bg-primary)', 
          color: 'var(--text-primary)', 
          border: '1px solid var(--border-color)',
          boxShadow: '0 10px 30px var(--shadow-color)'
        }}>
          <h3 style={{ color: 'var(--primary-color)', marginBottom: '10px', fontSize: '18px' }}>📋 {t('totalMenus')}</h3>
          <p style={{ fontSize: '48px', fontWeight: '700', margin: '0', color: 'var(--text-primary)' }}>{stats.menus}</p>
          <p style={{ color: 'var(--text-secondary)', marginTop: '10px', fontSize: '14px' }}>{t('restaurantSections')}</p>
        </div>

        <div className="card" style={{ 
          background: 'var(--bg-primary)', 
          color: 'var(--text-primary)', 
          border: '1px solid var(--border-color)',
          boxShadow: '0 10px 30px var(--shadow-color)'
        }}>
          <h3 style={{ color: 'var(--primary-color)', marginBottom: '10px', fontSize: '18px' }}>🍽️ {t('totalCategories')}</h3>
          <p style={{ fontSize: '48px', fontWeight: '700', margin: '0', color: 'var(--text-primary)' }}>{stats.categories}</p>
          <p style={{ color: 'var(--text-secondary)', marginTop: '10px', fontSize: '14px' }}>{t('foodCategories')}</p>
        </div>

        <div className="card" style={{ 
          background: 'var(--bg-primary)', 
          color: 'var(--text-primary)', 
          border: '1px solid var(--border-color)',
          boxShadow: '0 10px 30px var(--shadow-color)'
        }}>
          <h3 style={{ color: 'var(--primary-color)', marginBottom: '10px', fontSize: '18px' }}>🍕 {t('totalDishes')}</h3>
          <p style={{ fontSize: '48px', fontWeight: '700', margin: '0', color: 'var(--text-primary)' }}>{stats.dishes}</p>
          <p style={{ color: 'var(--text-secondary)', marginTop: '10px', fontSize: '14px' }}>{t('availableDishes')}</p>
        </div>

        <div className="card" style={{ 
          marginTop: '30px', 
          background: 'var(--bg-primary)', 
          color: 'var(--text-primary)', 
          border: '1px solid var(--border-color)',
          boxShadow: '0 10px 30px var(--shadow-color)'
        }}>
          <h3 style={{ marginBottom: '20px', color: 'var(--primary-color)', fontSize: '20px' }}>🎯 {t('quickActions')}</h3>
          <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/add-item')}
            >
              ➕ {t('addNewDish')}
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => navigate('/reports')}
            >
              📊 {t('viewReports')}
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => navigate('/settings')}
            >
              ⚙️ {t('settings')}
            </button>
          </div>
        </div>

        <div className="card" style={{ 
          marginTop: '20px', 
          background: 'var(--bg-primary)', 
          color: 'var(--text-primary)', 
          border: '1px solid var(--border-color)',
          boxShadow: '0 10px 30px var(--shadow-color)'
        }}>
          <h3 style={{ marginBottom: '20px', color: 'var(--primary-color)', fontSize: '20px' }}>📈 {t('systemOverview')}</h3>
          <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6', fontSize: '15px' }}>
            {t('welcomeMessage')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
